// 14章 金属工事 — やさしい解説
// 原文は src/spec_index.json（PDFから自動生成）。このファイルは手書きで管理する。
// SVGは currentColor を使い、ライト/ダーク両テーマに追従させる。

import type { Commentary } from "../types";

const SVG_ANCHOR_TEST = `
<svg viewBox="0 0 660 240" role="img" aria-label="あと施工アンカーの引張試験。1ロットから無作為に3本抜き取り、定めた強度まで引っ張る。全て合格でロット合格。不合格なら作業中止し原因調査、20パーセント追加抜取り">
  <g fill="currentColor" opacity="0.25"><rect x="40" y="150" width="580" height="50"/></g>
  <g fill="currentColor" opacity="0.7">
    <rect x="150" y="120" width="14" height="30"/>
    <rect x="330" y="120" width="14" height="30"/>
    <rect x="510" y="120" width="14" height="30"/>
  </g>
  <g stroke="currentColor" stroke-width="2">
    <line x1="157" y1="120" x2="157" y2="70"/>
    <line x1="337" y1="120" x2="337" y2="70"/>
    <line x1="517" y1="120" x2="517" y2="70"/>
  </g>
  <g fill="currentColor">
    <polygon points="157,64 151,76 163,76"/>
    <polygon points="337,64 331,76 343,76"/>
    <polygon points="517,64 511,76 523,76"/>
  </g>
  <g fill="currentColor" font-size="12.5" text-anchor="middle" opacity="0.85">
    <text x="157" y="55">引張</text>
    <text x="337" y="55">引張</text>
    <text x="517" y="55">引張</text>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="40" y="40" font-weight="bold">あと施工アンカーの引張試験（14.1.3）</text>
    <text x="40" y="228">1ロット（同一施工条件）から無作為に3本抜取り → 定めた強度まで引張り → 全て合格でロット合格</text>
  </g>
</svg>`;

const SVG_GALV = `
<svg viewBox="0 0 660 210" role="img" aria-label="鉄鋼の亜鉛めっきの種別。溶融亜鉛めっきはA種が板厚6ミリ以上、B種3.2ミリ以上、C種1.6ミリ以上。電気亜鉛めっきはD種からF種。板厚が厚いほど厚いめっきが付く">
  <g fill="currentColor" opacity="0.55"><rect x="60" y="70" width="120" height="24"/></g>
  <g fill="currentColor" opacity="0.4"><rect x="60" y="66" width="120" height="4"/><rect x="60" y="94" width="120" height="4"/></g>
  <g fill="currentColor" font-size="12.5">
    <text x="60" y="45" font-weight="bold">溶融亜鉛めっき（どぶ漬け）</text>
    <text x="200" y="76">A種：板厚6mm以上（HDZT77）</text>
    <text x="200" y="98">B種：3.2mm以上／C種：1.6mm以上</text>
    <text x="60" y="140" font-weight="bold">電気亜鉛めっき</text>
    <text x="200" y="140">D〜F種（クロメート皮膜）</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="60" y="180">補修可能な不めっき・剥離：総面積が有効面の0.5%以下かつ各10cm²以下。超えたら再めっき（14.2.2）</text>
    <text x="60" y="200" opacity="0.85">補修は高濃度亜鉛末塗料または亜鉛溶射。異種金属の接触腐食は防止する（14.1.3）</text>
  </g>
</svg>`;

const SVG_CEILING_LGS = `
<svg viewBox="0 0 660 270" role="img" aria-label="軽量鉄骨天井下地の構成。スラブのインサートにつりボルトをねじ込み、ハンガで野縁受けを吊り、クリップで野縁を留める。つりボルト・野縁受けの間隔は900ミリ程度、周辺部は端から150ミリ以内">
  <g fill="currentColor" opacity="0.25"><rect x="40" y="40" width="580" height="22"/></g>
  <g stroke="currentColor" stroke-width="3" stroke-linecap="round">
    <line x1="150" y1="62" x2="150" y2="140"/>
    <line x1="360" y1="62" x2="360" y2="140"/>
    <line x1="510" y1="62" x2="510" y2="140"/>
  </g>
  <g fill="currentColor" opacity="0.7"><rect x="110" y="140" width="440" height="14"/></g>
  <g fill="currentColor" opacity="0.85">
    <rect x="180" y="154" width="24" height="18"/>
    <rect x="330" y="154" width="24" height="18"/>
    <rect x="470" y="154" width="24" height="18"/>
  </g>
  <g fill="currentColor" opacity="0.4"><rect x="110" y="190" width="440" height="16"/></g>
  <g stroke="currentColor" stroke-width="1.5">
    <line x1="150" y1="120" x2="360" y2="120"/>
    <line x1="150" y1="112" x2="150" y2="128"/><line x1="360" y1="112" x2="360" y2="128"/>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="80" y="55" opacity="0.85">スラブ・インサート</text>
    <text x="215" y="116" font-weight="bold" text-anchor="middle">間隔900mm程度</text>
    <text x="160" y="100">つりボルト（呼び径9mm）</text>
    <text x="560" y="150" opacity="0.85">野縁受け</text>
    <text x="560" y="172" opacity="0.85">クリップ＋野縁</text>
    <text x="560" y="204" opacity="0.85">ボード</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="40" y="245">クリップのつめは野縁受けに対し交互に。野縁のはね出し150mm以内・野縁受けのはね出し300mm以内（14.4）</text>
    <text x="40" y="264" opacity="0.85">天井ふところ1.5ｍ以上はつりボルトの水平・斜め補強。特定天井は対象外（別途耐震規定）</text>
  </g>
</svg>`;

const SVG_WALL_LGS = `
<svg viewBox="0 0 660 260" role="img" aria-label="軽量鉄骨壁下地の構成。上下にランナを固定し、スタッドを差し込んで立てる。振れ止めを約1.2メートルごとに通し、スペーサでスタッドを押さえる。スタッド間隔は下地張りありで450ミリ、直張りで300ミリ">
  <g fill="currentColor" opacity="0.7">
    <rect x="80" y="50" width="440" height="16"/>
    <rect x="80" y="210" width="440" height="16"/>
  </g>
  <g stroke="currentColor" stroke-width="5" stroke-linecap="round">
    <line x1="140" y1="66" x2="140" y2="210"/>
    <line x1="240" y1="66" x2="240" y2="210"/>
    <line x1="340" y1="66" x2="340" y2="210"/>
    <line x1="440" y1="66" x2="440" y2="210"/>
  </g>
  <g stroke="currentColor" stroke-width="2" stroke-dasharray="6 4" opacity="0.8">
    <line x1="140" y1="140" x2="440" y2="140"/>
  </g>
  <g stroke="currentColor" stroke-width="1.5">
    <line x1="140" y1="90" x2="240" y2="90"/>
    <line x1="140" y1="82" x2="140" y2="98"/><line x1="240" y1="82" x2="240" y2="98"/>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="80" y="42" opacity="0.85">上部ランナ（梁下・スラブ下）</text>
    <text x="80" y="245" opacity="0.85">下部ランナ（床）</text>
    <text x="190" y="80" font-weight="bold" text-anchor="middle">間隔</text>
    <text x="460" y="145" opacity="0.85">振れ止め（約1.2ｍごと）</text>
    <text x="150" y="180" opacity="0.85">スタッド</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="80" y="30" font-weight="bold">軽量鉄骨壁下地（間仕切壁）</text>
  </g>
  <g fill="currentColor" font-size="11.5" opacity="0.8">
    <text x="520" y="130">スタッド間隔</text>
    <text x="520" y="148">下地張りあり450mm</text>
    <text x="520" y="166">直張り300mm</text>
  </g>
</svg>`;

const ch14: Record<string, Commentary> = {
  "14.1.1": {
    plainSummary:
      "この章は各種金属の表面処理・金属製品の製作・取付け工事に適用する。1章とセットで適用する。",
    why: "金属工事は「表面処理（めっき・陽極酸化）で錆から守る」ことと「天井・壁の軽量鉄骨下地」「笠木・成形板の取付け」が主な内容です。異種金属を接触させると腐食するなど、金属特有の注意点があります。",
  },

  "14.1.2": {
    plainSummary:
      "ゴールは3つ。①所定の材料 ②製品が所定の形状・寸法で所定の位置に堅固に取り付けられている ③所要の仕上り状態であること。",
  },

  "14.1.3": {
    plainSummary:
      "受材は構造体施工時に取り付ける（やむを得ずあと付けはあと施工アンカーで、防水層を傷めない）。あと施工アンカーは引張試験で確認（1ロット3本を無作為抜取り、定めた強度まで引張り、全数合格でロット合格。不合格なら作業中止・原因調査・20%追加抜取り）。異種金属は接触腐食を防止する。",
    why: "受材（下地金物）は本来コンクリートを打つ前に埋め込むのが理想。後から穴をあけて留めるあと施工アンカーは、効きが不確実なので引張試験で全ロット確認します。異種金属の接触腐食（電食）は、アルミと鉄など電位差のある金属が触れると片方が急速に錆びる現象で、絶縁材やパッキンで防ぎます。",
    points: [
      "あと施工アンカーの引張試験：1ロット3本、全数合格でロット合格",
      "アルミ×鉄などの接触部は絶縁して電食を防ぐ",
      "穿孔で使わなかった孔はモルタル充填",
    ],
    glossary: [
      {
        term: "接触腐食（電食）",
        meaning:
          "電位差のある異種金属が接触し、水分を介して片方が優先的に腐食する現象。",
      },
    ],
    diagrams: [
      {
        svg: SVG_ANCHOR_TEST,
        caption: "あと施工アンカーの引張試験（14.1.3）",
      },
    ],
  },

  "14.1.4": {
    plainSummary:
      "金属製品はポリエチレンフィルム等で養生して搬入。取付け後の出隅など損傷のおそれのある部分は当て板等で養生。工事完成時に養生材を取り除き清掃する。",
  },

  "14.2.1": {
    plainSummary:
      "アルミニウム・アルミ合金の表面処理は表14.2.1により種別は特記（陽極酸化皮膜AB種、陽極酸化塗装複合皮膜BA〜BC種等、環境の過酷さで選ぶ）。着色は特記がなければ二次電解着色。",
    why: "アルミは表面に酸化皮膜（アルマイト）を電気的に作って耐食性を高めます。屋外の過酷な環境ほど厚い複合皮膜（塗装も併用）を使う、という環境ランク別の選択が基本。サッシや笠木の色や耐久性はこの表面処理で決まります。",
    glossary: [
      {
        term: "陽極酸化皮膜（アルマイト）",
        meaning:
          "アルミを電解処理して表面に作る硬い酸化皮膜。耐食性・耐摩耗性を高める。",
      },
    ],
  },

  "14.2.2": {
    plainSummary:
      "鉄鋼の亜鉛めっきは表14.2.2により種別は特記。溶融亜鉛めっきはA種（板厚6mm以上）・B種（3.2mm以上）・C種（1.6mm以上）、電気亜鉛めっきはD〜F種。補修可能な不めっき・剥離は総面積0.5%以下かつ各10cm²以下（超えたら再めっき）。補修は高濃度亜鉛末塗料か亜鉛溶射。",
    why: "亜鉛めっきは鉄を錆から守る犠牲防食（亜鉛が先に錆びて鉄を守る）が働きます。板厚が厚いほど厚いめっきが付き（A>B>C種）、耐久性が上がります。補修に高濃度亜鉛末塗料を使うのも、亜鉛の犠牲防食効果を残すためです。",
    points: [
      "溶融亜鉛めっきA/B/C種は板厚6/3.2/1.6mmが目安",
      "補修は高濃度亜鉛末塗料（ジンクリッチ）か亜鉛溶射",
    ],
    glossary: [
      {
        term: "溶融亜鉛めっき",
        meaning: "溶けた亜鉛に鋼材を浸けてめっきする方法（どぶ漬け）。厚く付き耐久性が高い。",
      },
      {
        term: "犠牲防食",
        meaning: "亜鉛が鉄より先に錆びることで、下の鉄の腐食を防ぐしくみ。",
      },
    ],
    diagrams: [
      {
        svg: SVG_GALV,
        caption: "亜鉛めっきの種別（14.2.2）",
      },
    ],
  },

  "14.3.1": {
    plainSummary:
      "ステンレス・アルミ・アルミ合金の溶接は原則工場溶接。溶接・ろう付けは治具を用いて確実に行う。",
    why: "ステンレスやアルミの溶接は熱管理がシビアで歪みやすいため、設備の整った工場で行うのが原則です。",
  },

  "14.3.2": {
    plainSummary: "鉄鋼の溶接は7章（鉄骨工事）に準ずる。",
  },

  "14.3.3": {
    plainSummary:
      "アルミ・アルミ合金の溶接はJIS Z 3232の溶加棒でJIS Z 3604のイナートガスアーク溶接による。ろう付けはJIS Z 3263のろう材。技能者は相応の技量・経験・知識を持つ者。",
  },

  "14.3.4": {
    plainSummary:
      "ステンレスの溶接材料は母材・溶接方法に適したもの。ろう材はJIS Z 3261（銀ろう）またはJIS Z 3282（はんだ）。技能者は相応の技量・経験・知識を持つ者。",
  },

  "14.4.1": {
    plainSummary:
      "この節は屋内・屋外の軽量鉄骨天井下地に適用する。ただし特定天井、質量20kg/m²超の天井、水平でない天井、システム天井は除く。",
    why: "特定天井（脱落で重大被害のおそれがある大規模な吊り天井）を除外しているのは重要。東日本大震災で天井落下事故が多発したため、特定天井には別途厳しい耐震規定が適用されます。",
    glossary: [
      {
        term: "特定天井",
        meaning:
          "高さ6m超・面積200m²超・質量2kg/m²超などの吊り天井。地震での脱落防止のため構造方法が法で定められている。",
      },
    ],
  },

  "14.4.2": {
    plainSummary:
      "天井下地材はJIS A 6517による。野縁等は特記がなければ屋内19形・屋外25形。補強部材・インサートは防錆処理されたもの。",
    glossary: [
      {
        term: "野縁（のぶち）・野縁受け",
        meaning:
          "天井ボードを留める格子状の下地。野縁受けに直交して野縁を渡す。",
      },
    ],
  },

  "14.4.3": {
    plainSummary:
      "野縁受け・つりボルト・インサートの間隔は900mm程度、周辺部は端から150mm以内。野縁の間隔は仕上げの種類で決まる（ボード直張りは300mm程度、下地張りありは360mm程度）。",
    points: [
      "つりボルト・野縁受けは900mm程度、周辺は端から150mm以内",
      "野縁間隔はボード直張り300mm程度が基本",
    ],
  },

  "14.4.4": {
    plainSummary:
      "インサートは型枠組立時に配置。つりボルトはインサートにねじ込むか鉄骨に溶接。野縁はハンガの野縁受けにクリップで留める（クリップのつめは交互向き）。開口部は同材で補強。野縁のはね出しは150mm以内、野縁受けのはね出しは300mm以内。天井ふところ1.5ｍ以上はつりボルトの水平補強（1.8ｍ程度）・斜め補強（3.6ｍ程度）を行う。溶接箇所はAz種錆止め塗料。",
    why: "天井は地震で最も落ちやすい部分の一つ。クリップのつめを交互向きにするのは、振動で外れにくくするためです。天井ふところ（スラブから天井までの空間）が深いほど揺れやすいので、1.5ｍ以上は斜め補強でブレース効果を持たせます。はね出し制限は、端部の垂れ下がりを防ぎます。",
    points: [
      "クリップのつめは交互向き（外れ防止）",
      "野縁はね出し150mm以内・野縁受けはね出し300mm以内",
      "天井ふところ1.5ｍ以上は水平・斜め補強",
    ],
    glossary: [
      {
        term: "天井ふところ",
        meaning: "上階スラブ（梁）の下端から天井仕上げ面までの空間。",
      },
      {
        term: "ハンガ・クリップ",
        meaning:
          "つりボルトに野縁受けを掛ける金物がハンガ、野縁受けに野縁を留める金物がクリップ。",
      },
    ],
    diagrams: [
      {
        svg: SVG_CEILING_LGS,
        caption: "軽量鉄骨天井下地の構成（14.4.4）",
      },
    ],
  },

  "14.5.1": {
    plainSummary: "この節は屋内の間仕切壁等の軽量鉄骨壁下地（LGS）に適用する。",
    glossary: [
      {
        term: "軽量鉄骨壁下地（LGS）",
        meaning:
          "薄い鋼板を成形したスタッド・ランナで組む間仕切壁の骨組み。木下地より不燃・寸法安定に優れる。",
      },
    ],
  },

  "14.5.2": {
    plainSummary:
      "壁下地材はJIS A 6517による。開口部補強材・取付け金物・打込みピン・小ねじ・ボルト等は防錆処理されたもの。",
  },

  "14.5.3": {
    plainSummary:
      "スタッド・ランナ等は表14.5.1により、種類は特記がなければスタッドの高さで区分（50形＝高さ2.7ｍ以下、65形＝4.0ｍ以下、90形＝4.5ｍ以下、100形＝5.0ｍ以下）。スタッド間隔は下地張りありで450mm程度、直張りで300mm程度。",
    why: "壁が高いほど太いスタッドが必要（座屈しないため）で、高さで形番を選びます。この対応（2.7/4.0/4.5/5.0ｍ）は実務・試験の頻出知識です。",
    points: [
      "スタッドは高さで選ぶ：50形2.7ｍ／65形4.0ｍ／90形4.5ｍ／100形5.0ｍ",
      "スタッド間隔は直張り300mm・下地張りあり450mm",
    ],
    glossary: [
      {
        term: "スタッド・ランナ",
        meaning:
          "壁の縦材がスタッド、上下に渡す横材（受け）がランナ。ランナにスタッドを差し込んで立てる。",
      },
    ],
  },

  "14.5.4": {
    plainSummary:
      "ランナは端部を押さえ間隔900mm程度で床・梁下・スラブ下に固定。スタッドの上下をランナに差し込む。振れ止めは床ランナ下端から約1.2ｍごと（上部ランナ上端から400mm以内なら省略可）。スペーサは各スタッドの端部を押さえ間隔600mm程度。出入口・開口部は補強。溶接箇所はAz種錆止め塗料。",
    why: "スタッドはただ立てるだけでは揺れるので、振れ止め（水平の連結材）で一体化します。約1.2ｍごとの振れ止めが壁全体の剛性を保ちます。開口部（ドア・ダクト）は枠周りを補強しないと、開閉の衝撃や荷重でスタッドが変形します。",
    points: [
      "振れ止めは約1.2ｍごと",
      "ランナは900mm程度で固定、スペーサは600mm程度",
      "出入口・ダクト開口部は補強材で固める",
    ],
    diagrams: [
      {
        svg: SVG_WALL_LGS,
        caption: "軽量鉄骨壁下地の構成（14.5.4）",
      },
    ],
  },

  "14.6.1": {
    plainSummary: "この節は建築物の天井の金属成形板張りに適用する。",
    glossary: [
      {
        term: "金属成形板",
        meaning:
          "アルミ・鋼板を成形した天井パネル（システム天井のパネル等）。軒天やピロティ天井にも使う。",
      },
    ],
  },

  "14.6.2": {
    plainSummary:
      "金属成形板の種別・表面処理は特記。取付けの小ねじ類は使用材料に適したもの。",
  },

  "14.6.3": {
    plainSummary:
      "取付け用下地は特記がなければ4節（軽量鉄骨天井下地）による。取付け前に割付けを行い、切り粉は直ちに除去。取付けは下地ごとに隠し小ねじ留め。長尺物の伸縮調整継手は特記。",
    why: "金属は温度で伸び縮みするため、長尺物には伸縮調整継手が必要です。切り粉（切断で出る鉄粉）を放置すると、そこから錆が発生して「もらい錆」になるため直ちに除去します。",
  },

  "14.7.1": {
    plainSummary:
      "この節は屋上パラペット等に使うオープン形式のアルミニウム製笠木に適用する。",
    glossary: [
      {
        term: "笠木（かさぎ）",
        meaning: "パラペットや手すり壁の天端を覆う仕上げ材。雨水の浸入を防ぐ。",
      },
    ],
  },

  "14.7.2": {
    plainSummary:
      "アルミ製笠木の種類は表14.7.1により部材の種類は特記（250形・300形・350形、笠木本体はA6063S）。役物・付属部品は製造所仕様。表面処理は表14.2.1による。",
  },

  "14.7.3": {
    plainSummary:
      "固定金具はあと施工アンカーで風圧力に対応して堅固に取り付ける（下地モルタルの上はコンクリート部分へのアンカー長さを確保）。笠木本体と固定金具ははめあい方式・ボルト締付け等。継手はジョイント金具のはめあい方式。コーナー部は留め加工＋溶接・裏板補強＋止水処理。",
    why: "笠木の要点は「風で飛ばない固定」と「継手からの止水」。オープン形式は笠木本体が固定金具にカチッとはまる（はめあい）構造で、温度伸縮を逃がしつつ雨仕舞いを確保します。コーナーと継手が雨漏りの弱点なので止水処理が重要です。",
    points: [
      "固定金具はあと施工アンカーで風圧力対応（アンカー長さ確保）",
      "はめあい方式で温度伸縮を逃がす",
      "コーナー・継手部の止水処理が雨仕舞いの要",
    ],
    diagrams: [],
  },
};

export default ch14;
